import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Activity, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Loader2,
  Database,
  Server
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const API_V4_PREFIX = "/v4/99food";

const Didi99FoodDiagnostic = ({ connectionId }) => {
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDiagnostic = async () => {
    if (!connectionId) {
      toast.error("Nenhuma conexão configurada");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`${API_V4_PREFIX}/connections/${connectionId}/runtime-diagnostic`);
      setDiagnostic(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar diagnóstico");
      toast.error("Erro ao carregar diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === "OK") return "bg-green-50 border-green-300";
    if (status === "ATENÇÃO") return "bg-yellow-50 border-yellow-300";
    return "bg-red-50 border-red-300";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Diagnóstico Runtime
            </CardTitle>
            <CardDescription>Estado real da integração em tempo de execução</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadDiagnostic} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Atualizar</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">Erro ao carregar diagnóstico</span>
            </div>
            <p className="mt-1 text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {loading && !diagnostic && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        )}
        
        {diagnostic && (
          <div className="space-y-6">
            <div className={`p-4 rounded-lg border-2 ${getStatusColor(diagnostic.status_real)}`}>
              <div className="flex items-center gap-3">
                {diagnostic.status_real === "OK" ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : diagnostic.status_real === "ATENÇÃO" ? (
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
                <div>
                  <h3 className="font-bold text-lg">Status: {diagnostic.status_real}</h3>
                  <p className="text-sm text-gray-600">
                    Última verificação: {new Date(diagnostic.timestamp).toLocaleString("pt-BR")}
                  </p>
                </div>
              </div>
            </div>

            {diagnostic.divergencias && diagnostic.divergencias.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-bold text-red-800 flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5" />
                  DIVERGÊNCIAS DETECTADAS
                </h4>
                <ul className="space-y-2">
                  {diagnostic.divergencias.map((d, i) => (
                    <li key={i} className="text-sm text-red-700 p-2 bg-red-100 rounded">
                      <strong>{d.campo}:</strong> {d.mensagem}
                      {d.esperado && (
                        <div className="mt-1 text-xs">
                          <span className="text-gray-600">Esperado:</span> {d.esperado}<br/>
                          <span className="text-gray-600">Atual:</span> {d.atual}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Configuração Salva (Banco)
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">App ID:</span>
                    <code className="bg-gray-200 px-2 py-0.5 rounded">{diagnostic.saved_config?.app_id || "-"}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Merchant ID:</span>
                    <code className="bg-gray-200 px-2 py-0.5 rounded">{diagnostic.saved_config?.merchant_id || "-"}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client ID:</span>
                    <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">{diagnostic.saved_config?.client_id || "-"}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Modo:</span>
                    <Badge variant="outline">{diagnostic.saved_config?.mode || "-"}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Autenticado:</span>
                    <Badge className={diagnostic.saved_config?.is_authenticated ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                      {diagnostic.saved_config?.is_authenticated ? "Sim" : "Não"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Token em Cache
                </h4>
                {diagnostic.token_cache?.exists ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <Badge className={diagnostic.token_cache.is_expired ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                        {diagnostic.token_cache.is_expired ? "EXPIRADO" : "VÁLIDO"}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expira em:</span>
                      <span>{new Date(diagnostic.token_cache.expires_at).toLocaleString("pt-BR")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Renovações:</span>
                      <span>{diagnostic.token_cache.refresh_count}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p>Nenhum token em cache</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 text-sm mb-2">Última Autenticação</h4>
                {diagnostic.last_auth ? (
                  <div className="text-sm">
                    <p className="text-blue-700">{new Date(diagnostic.last_auth.at).toLocaleString("pt-BR")}</p>
                    <Badge variant={diagnostic.last_auth.status_code === 200 ? "default" : "destructive"}>
                      HTTP {diagnostic.last_auth.status_code || "N/A"}
                    </Badge>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum registro</p>
                )}
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 text-sm mb-2">Último Webhook</h4>
                {diagnostic.last_webhook ? (
                  <div className="text-sm">
                    <p className="text-purple-700">{new Date(diagnostic.last_webhook.at).toLocaleString("pt-BR")}</p>
                    <Badge variant="outline">HTTP {diagnostic.last_webhook.status_code || "N/A"}</Badge>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum webhook recebido</p>
                )}
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-800 text-sm mb-2">Último Polling</h4>
                {diagnostic.last_polling ? (
                  <div className="text-sm">
                    <p className="text-orange-700">{new Date(diagnostic.last_polling.at).toLocaleString("pt-BR")}</p>
                    <Badge variant="outline">HTTP {diagnostic.last_polling.status_code || "N/A"}</Badge>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum polling executado</p>
                )}
              </div>
            </div>

            {diagnostic.events_by_status && Object.keys(diagnostic.events_by_status).length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-bold text-gray-800 mb-3">Eventos por Status</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(diagnostic.events_by_status).map(([status, count]) => (
                    <Badge key={status} variant="outline">{status}: {count}</Badge>
                  ))}
                </div>
              </div>
            )}

            {diagnostic.recent_events && diagnostic.recent_events.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-bold text-gray-800 mb-3">Eventos Recentes</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Tipo</th>
                        <th className="text-left p-2">Order ID</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Origem</th>
                        <th className="text-left p-2">Recebido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostic.recent_events.map((event, i) => (
                        <tr key={i} className="border-b hover:bg-gray-100">
                          <td className="p-2"><Badge variant="outline">{event.event_type}</Badge></td>
                          <td className="p-2 font-mono text-xs">{event.order_id?.substring(0, 12)}...</td>
                          <td className="p-2">
                            <Badge className={
                              event.status === "ACKED" ? "bg-green-100 text-green-800" :
                              event.status === "PENDING" ? "bg-yellow-100 text-yellow-800" :
                              event.status === "FAILED" ? "bg-red-100 text-red-800" :
                              "bg-gray-100 text-gray-800"
                            }>{event.status}</Badge>
                          </td>
                          <td className="p-2">{event.source}</td>
                          <td className="p-2 text-xs">{new Date(event.received_at).toLocaleString("pt-BR")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {diagnostic.worker_status && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Status do Worker
                </h4>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">Rodando:</span>
                  <Badge className={diagnostic.worker_status.running ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                    {diagnostic.worker_status.running ? "Sim" : "Não"}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}

        {!diagnostic && !loading && !error && (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Clique em "Atualizar" para carregar o diagnóstico</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Didi99FoodDiagnostic;
